/**
 * Picture-naming archetype renderer (the existing #251 player,
 * moved here so the dispatcher can pick it like any other).
 *
 * Image + "Say it" (Web Speech API where supported) + 4 tap-word
 * choices. Self-correction window: 3s. Cue flag: shown when the
 * kid taps "Show me a hint".
 *
 * The renderer reads trial shape from `activity_payload.trials`
 * (built by `buildPictureNamingTrials`), which is the format #251
 * already shipped. The rubric here is the minimal
 * `PictureNamingRubric` (just an SLP-review flag) — picture-
 * naming is shape-defined by the trial payload, not the rubric.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import type { PictureNamingRubric } from '../../../lib/practice/rubrics';
import { scorePictureNaming } from '../../../lib/practice/scoring';
import type { PictureNamingTrial } from './pictureNamingTrials';
import type { ArchetypePlayerProps, TrialOutcome } from './shared';
import { BulbIcon } from '../../../components/icons/BulbIcon';
import { PhotoIcon } from '../../../components/icons/PhotoIcon';
import { SpeakerIcon } from '../../../components/icons/SpeakerIcon';

interface Props extends Omit<ArchetypePlayerProps<PictureNamingRubric>, 'rubric'> {
  rubric: PictureNamingRubric;
  /** Trial built from the activity_payload.trials array. */
  trial: PictureNamingTrial;
}

/**
 * Self-correction window for picture_naming — 3s, matching the
 * #251 inline player (kept identical to avoid changing the
 * existing shipped behavior).
 */
const SELF_CORRECT_WINDOW_MS = 3_000;

export default function PictureNamingPlayer({
  trial,
  trialIndex,
  totalTrials,
  onTrialComplete,
}: Props): ReactElement {
  const [shownHint, setShownHint] = useState(false);
  const [startedAt, setStartedAt] = useState<number>(() => Date.now());
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
  const [lastTapAt, setLastTapAt] = useState<number | null>(null);
  const [hasSelfCorrected, setHasSelfCorrected] = useState(false);

  // Reset per-trial state. Keyed on `trialIndex` only — the
  // `trial` prop's identity is NOT stable (the router rebuilds the
  // trials array on every render), so depending on it would wipe
  // the kid's in-progress selection any time the parent re-renders
  // (fix #302). Matches `ConversationTurns` / `ReceptiveDirections`
  // and the pre-#252 `PlayingPhase` behavior; per-archetype-player
  // contract in `./shared.ts` documents this as the canonical key.
  useEffect(() => {
    setStartedAt(Date.now());
    setShownHint(false);
    setSelectedChoice(null);
    setLastTapAt(null);
    setHasSelfCorrected(false);
  }, [trialIndex]);

  function speak(): void {
    if (typeof window === 'undefined') return;
    const Speech = (window as unknown as { SpeechSynthesisUtterance?: unknown })
      .SpeechSynthesisUtterance;
    if (!Speech) return;
    const synth = window.speechSynthesis;
    if (!synth || typeof synth.speak !== 'function') return;
    try {
      const utter = new (Speech as new (opts: { text: string }) => unknown)({
        text: `Say this picture's name: ${trial.expected}`,
      });
      synth.speak(utter as never);
    } catch {
      // Web Speech API is unreliable on iOS in-app browsers — fail
      // silently rather than block the kid on a read-aloud.
    }
  }

  function onTap(choice: string): void {
    const now = Date.now();
    if (selectedChoice === null) {
      setLastTapAt(now);
    } else if (
      choice !== selectedChoice &&
      lastTapAt !== null &&
      now - lastTapAt < SELF_CORRECT_WINDOW_MS
    ) {
      setHasSelfCorrected(true);
      setLastTapAt(now);
    } else {
      setLastTapAt(now);
    }
    setSelectedChoice(choice);
  }

  function onGotIt(): void {
    if (selectedChoice === null) return;
    const now = Date.now();
    const score = scorePictureNaming(selectedChoice, trial.expected);
    const outcome: TrialOutcome = {
      isCorrect: score.is_correct,
      latencyMs: now - startedAt,
      selfCorrected: hasSelfCorrected,
      usedCue: shownHint,
      response: selectedChoice,
      expected: trial.expected,
      promptKind: 'picture',
    };
    onTrialComplete(outcome);
  }

  // Compute the outcome-as-of-the-current-tap so the selected
  // choice button can show the kid a correct/incorrect cue the
  // instant they tap (fix #403: immediate reinforcement, without
  // waiting for "Got it!" — the visual cue persists from first
  // tap until the kid advances, comfortably exceeding the 2s
  // floor in the AC). Re-tapping within SELF_CORRECT_WINDOW_MS
  // re-runs the scoring against the new choice; the cue updates
  // accordingly. Note: we deliberately surface right/wrong here
  // rather than hiding the outcome — picture_naming is targeted
  // at kids old enough to process immediate correctness feedback,
  // which is the entire pedagogical point of an expressive-
  // naming trial.
  const isCorrect: boolean | null =
    selectedChoice === null
      ? null
      : scorePictureNaming(selectedChoice, trial.expected).is_correct;

  return (
    <main className="screen handoff">
      <div className="auth-hero">
        <header className="screen-header">
          <h1>Trial {trialIndex + 1} / {totalTrials}</h1>
          <p className="tagline">What&rsquo;s this picture?</p>
        </header>
      </div>
      {trial.image_url ? (
        <img
          src={trial.image_url}
          alt="Picture to name"
          className="prompt-image"
        />
      ) : (
        <div className="prompt-placeholder" aria-hidden="true">
          <PhotoIcon size={32} aria-label="Picture placeholder" />
        </div>
      )}
      <div className="action-row">
        <button
          type="button"
          className="link-button"
          onClick={speak}
        >
          <SpeakerIcon size={16} aria-hidden="true" />
          <span>Say it</span>
        </button>
        <button
          type="button"
          className="link-button"
          onClick={(): void => setShownHint(true)}
        >
          <BulbIcon size={16} aria-hidden="true" />
          <span>Show me a hint</span>
        </button>
      </div>
      {shownHint ? (
        <p className="tagline" role="status">
          Hint &mdash; starts with &ldquo;{trial.expected.slice(0, 1).toUpperCase()}&rdquo;
        </p>
      ) : null}
      <div className="choice-grid">
        {trial.choices.map((choice) => {
          const isSelected = selectedChoice === choice;
          const outcomeClass =
            isSelected && isCorrect === true
              ? ' selected correct'
              : isSelected && isCorrect === false
                ? ' selected incorrect'
                : selectedChoice !== null && !isSelected
                  ? ' muted'
                  : '';
          return (
            <button
              type="button"
              key={choice}
              className={`snap-button${outcomeClass}`}
              aria-pressed={isSelected}
              aria-label={
                isSelected && isCorrect === true
                  ? `Correct — ${choice}`
                  : isSelected && isCorrect === false
                    ? `Wrong — ${choice}; correct was ${trial.expected}`
                    : choice
              }
              onClick={(): void => onTap(choice)}
            >
              {choice}
            </button>
          );
        })}
      </div>
      {selectedChoice !== null ? (
        <button
          type="button"
          className="snap-button"
          onClick={onGotIt}
          aria-label={
            isCorrect === true
              ? `Got it! — correct, ${selectedChoice}`
              : isCorrect === false
                ? `Got it! — wrong, ${selectedChoice}; correct was ${trial.expected}`
                : 'Got it!'
          }
        >
          Got it!
        </button>
      ) : null}
    </main>
  );
}
